import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import {
  Box,
  ButtonBase,
  CardContent,
  ClickAwayListener,
  Grid,
  Paper,
  Popper,
  Stack,
  Typography,
  styled,
} from '@mui/material'
import { useAuthContext } from 'components/context/AuthProvider'
import { useRef, useState } from 'react'
import ProfileList from './ProfileList'
import Transitions from './Transitions'

const StyledButtonBase = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ theme, open }) => ({
  p: 0.25,
  bgcolor: open ? 'grey.300' : 'transparent',
  borderRadius: 1,
  '&:hover': { bgcolor: 'secondary.lighter' },
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: 290,
  minWidth: 240,
  maxWidth: 290,
  [theme.breakpoints.down('md')]: {
    maxWidth: 250,
  },
}))

const Profile = () => {
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return
    }
    setOpen(false)
  }

  const { profileName, position } = useAuthContext()

  return (
    <Box flexShrink={0} ml={0.75}>
      <StyledButtonBase
        open={open}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'client-forms-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack p={0.5} direction="row" spacing={1} alignItems="center">
          <AccountCircleOutlinedIcon />
          <Typography variant="body1">{profileName}</Typography>
        </Stack>
      </StyledButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            {open && (
              <StyledPaper>
                <ClickAwayListener onClickAway={handleClose}>
                  <Box>
                    <CardContent sx={{ px: 2.5, pt: 3 }}>
                      <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <AccountCircleOutlinedIcon />
                            <Stack>
                              <Typography variant="h6">{profileName}</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {position}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>

                    <ProfileList setOpen={setOpen} />
                  </Box>
                </ClickAwayListener>
              </StyledPaper>
            )}
          </Transitions>
        )}
      </Popper>
    </Box>
  )
}

export default Profile
